import { type FC, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { supabase } from '@/lib/api/supabase';
import { useEventRegistrationSettings } from '@/hooks';
import {
  bundleSavingsCents,
  computeSubtotalCents,
  validateCart,
  type DayPassOption,
  type PassEntry,
} from '@/features/dayPass/dayPassCart';

const dollars = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const MAX_PASSES = 20;

const CHECKOUT_ERRORS: Record<string, string> = {
  day_passes_disabled: "Day passes aren't on sale right now.",
  invalid_email: 'Enter a valid email address.',
  invalid_attendee_name: 'Enter a name for every pass.',
  invalid_cart: 'Something looks off with your cart. Refresh and try again.',
  invalid_item: 'Something looks off with your cart. Refresh and try again.',
  invalid_day_pass: 'One or more selected passes are no longer available. Please refresh.',
  day_pass_not_priced: 'One or more selected passes are not on sale yet. Please refresh.',
};
const CHECKOUT_FALLBACK = 'Could not start checkout. Please try again.';

/**
 * supabase.functions.invoke() throws on any non-2xx WITHOUT parsing the body: `data`
 * comes back null and `error.message` is always the generic "Edge Function returned a
 * non-2xx status code". The real reason code is on `error.context` (the raw Response).
 * Same approach as CactusCupShirts' readErrorBody.
 */
async function readErrorCode(error: unknown): Promise<string | null> {
  const ctx = (error as { context?: Response } | null)?.context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = await ctx.json();
      return typeof body?.error === 'string' ? body.error : null;
    } catch {
      /* body wasn't JSON */
    }
  }
  return null;
}

export const DayPassPage: FC = () => {
  const { settings, isLoading } = useEventRegistrationSettings();
  const [searchParams] = useSearchParams();
  const checkoutCancelled = searchParams.get('checkout') === 'cancel';
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [names, setNames] = useState<Record<string, string>>({}); // key `${id}:${index}`
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const options: DayPassOption[] = useMemo(
    () => settings.dayPasses.map((p) => ({
      id: p.id,
      name: p.name,
      fee: p.fee,
      // `?? []` / `?? false`: DayPass.covers/.highlight are optional, so reading them
      // yields `| undefined` even though the parser never actually produces undefined.
      // exactOptionalPropertyTypes forbids assigning an explicit `undefined` into the
      // optional DayPassOption fields, so we normalize here instead. Behavior-neutral:
      // bundleSavingsCents already treats a missing `covers` the same as `[]`, and the
      // badge check already treats a missing `highlight` the same as `false`.
      covers: p.covers ?? [],
      highlight: p.highlight ?? false,
    })),
    [settings.dayPasses],
  );

  const entries: PassEntry[] = useMemo(() => {
    const out: PassEntry[] = [];
    for (const opt of options) {
      const qty = quantities[opt.id] ?? 0;
      for (let i = 0; i < qty; i++) {
        out.push({ dayPassId: opt.id, attendeeName: names[`${opt.id}:${i}`] ?? '' });
      }
    }
    return out;
  }, [options, quantities, names]);

  const subtotal = computeSubtotalCents(entries, options);

  const setQty = (id: string, qty: number) => {
    const next = Math.max(0, Math.min(MAX_PASSES, qty));
    setQuantities((prev) => ({ ...prev, [id]: next }));
    // Drop names for slots that no longer exist. Without this, lowering the quantity
    // and raising it again resurrects names the buyer already removed — and a wrong
    // name on a pass means staff can't find that guest at the door.
    setNames((prev) => {
      const kept: Record<string, string> = {};
      for (const [key, value] of Object.entries(prev)) {
        const sep = key.lastIndexOf(':');
        const optId = key.slice(0, sep);
        const index = Number(key.slice(sep + 1));
        if (optId === id && index >= next) continue;
        kept[key] = value;
      }
      return kept;
    });
  };
  const setName = (key: string, value: string) =>
    setNames((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    setError(null);
    const check = validateCart(entries, options, email);
    if (!check.ok) { setError(check.error); return; }

    setSubmitting(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('daypass-create-checkout', {
        body: {
          email: email.trim(),
          items: entries.map((e) => ({ dayPassId: e.dayPassId, attendeeName: e.attendeeName.trim() })),
        },
      });
      if (fnError || !data?.url) {
        const code = fnError
          ? await readErrorCode(fnError)
          : (typeof data?.error === 'string' ? data.error : null);
        setError((code && CHECKOUT_ERRORS[code]) || CHECKOUT_FALLBACK);
        setSubmitting(false);
        return;
      }
      window.location.href = data.url as string;
    } catch {
      setError(CHECKOUT_FALLBACK);
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-orange-500 border-r-transparent"></div>
          <p className="mt-4 text-white">Loading day passes...</p>
        </div>
      </div>
    );
  }

  const available = settings.dayPassesEnabled && options.length > 0;

  return (
    <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
      <section className="container mx-auto px-4 pt-12 pb-6 text-center text-white">
        <h1 className="text-5xl font-viking mb-3 drop-shadow-lg">Day Passes</h1>
        <p className="max-w-2xl mx-auto text-lg drop-shadow-md">
          Coming for the whole weekend, or just a day? Buy spectator passes below.
          No account needed. Give your name at the door and we&apos;ll check you in.
        </p>
      </section>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {!available ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-white/90">Day passes aren&apos;t on sale right now. Check back soon!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {checkoutCancelled && (
              <p role="status" className="rounded-lg bg-amber-900/50 p-3 text-sm text-amber-100">
                Checkout cancelled. You haven&apos;t been charged. Your selections were cleared; pick your days again below.
              </p>
            )}
            {options.map((opt) => {
              const qty = quantities[opt.id] ?? 0;
              const savings = bundleSavingsCents(opt, options);
              const showBadge = opt.highlight === true && savings > 0;
              return (
                <Card key={opt.id}>
                  <CardContent className="py-6 space-y-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h2 className="text-2xl font-viking text-white">{opt.name}</h2>
                          {showBadge && (
                            <span className="rounded-full bg-orange-500 px-3 py-1 text-xs font-bold text-white">
                              Best value: save {dollars(savings)}
                            </span>
                          )}
                        </div>
                        <p className="text-orange-200">{dollars(opt.fee)} each</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button variant="outline" size="icon" aria-label={`Remove one ${opt.name}`}
                          onClick={() => setQty(opt.id, qty - 1)} disabled={qty === 0}>−</Button>
                        <span className="w-8 text-center text-white text-lg" aria-live="polite">{qty}</span>
                        <Button variant="outline" size="icon" aria-label={`Add one ${opt.name}`}
                          onClick={() => setQty(opt.id, qty + 1)}
                          disabled={entries.length >= MAX_PASSES}>+</Button>
                      </div>
                    </div>
                    {entries.length >= MAX_PASSES && qty > 0 && (
                      <p className="text-sm text-orange-200">Maximum {MAX_PASSES} passes per order.</p>
                    )}
                    {qty > 0 && (
                      <div className="space-y-2">
                        {Array.from({ length: qty }, (_, i) => (
                          <div key={i}>
                            <Label htmlFor={`${opt.id}-name-${i}`} className="text-orange-200">
                              Attendee name (pass {i + 1})
                            </Label>
                            <Input
                              id={`${opt.id}-name-${i}`}
                              value={names[`${opt.id}:${i}`] ?? ''}
                              onChange={(e) => setName(`${opt.id}:${i}`, e.target.value)}
                              placeholder="Full name"
                              maxLength={100}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            <Card>
              <CardContent className="py-6 space-y-4">
                <div>
                  <Label htmlFor="buyer-email" className="text-orange-200">Your email (for your receipt)</Label>
                  <Input id="buyer-email" type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
                </div>
                <div className="flex items-center justify-between text-white text-xl font-semibold">
                  <span>Total</span>
                  <span>{dollars(subtotal)}</span>
                </div>
                {error && <p role="alert" className="rounded-lg bg-red-900/60 p-3 text-sm text-red-100">{error}</p>}
                <Button variant="turquoise" size="lg" className="w-full"
                  onClick={handleSubmit} disabled={submitting || entries.length === 0}>
                  {submitting ? 'Starting checkout…' : `Pay ${dollars(subtotal)}`}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};
